import { useState, useRef, useCallback, useEffect } from 'react'
import { buildBackendUrl } from '@/lib/backend-url'
import { actions } from '@/store'

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [interimText, setInterimText] = useState('')
  const [rawInterimText, setRawInterimText] = useState('')
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const recognitionRef = useRef(null)
  const isStoppingRef = useRef(false)
  const lastCleanedTextRef = useRef('')
  const cleanTimeoutRef = useRef(null)

  // Smart cleaning of interim text
  const cleanInterimText = useCallback(
    async (text) => {
      if (!text || text.trim().length < 5 || text === lastCleanedTextRef.current) return

      try {
        const token = localStorage.getItem('zoy_auth_token')
        const response = await fetch(buildBackendUrl('/api/ai/clean-transcript'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ text }),
        })

        if (response.ok) {
          const result = await response.json()
          if (result.text && isRecording) {
            setInterimText(result.text)
            lastCleanedTextRef.current = text
          }
        }
      } catch (err) {
        // Ignore background cleaning errors
      }
    },
    [isRecording],
  )

  // Initialize Speech Recognition for real-time feedback
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'

      recognition.onresult = (event) => {
        let currentInterim = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          currentInterim += transcript
        }

        if (currentInterim) {
          setRawInterimText(currentInterim)
          // Always show raw immediately so user sees responsiveness
          setInterimText(currentInterim)

          // Debounced cleaning for smart corrections ("no i mean", etc)
          if (cleanTimeoutRef.current) clearTimeout(cleanTimeoutRef.current)
          cleanTimeoutRef.current = setTimeout(() => {
            cleanInterimText(currentInterim)
          }, 1000)
        }
      }

      recognition.onerror = (event) => {
        if (event.error === 'not-allowed') {
          console.error('Microphone permission denied')
        } else if (event.error !== 'no-speech') {
          console.error('Speech recognition error:', event.error)
        }
      }

      recognition.onend = () => {
        // Automatically restart if we are still supposed to be recording
        if (isRecording && !isStoppingRef.current) {
          try {
            recognition.start()
          } catch (e) {
            // Ignore if already started
          }
        }
      }

      recognitionRef.current = recognition
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onend = null
        recognitionRef.current.stop()
      }
    }
  }, [isRecording])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // High-quality recording for Whisper
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      isStoppingRef.current = false

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.start()

      // Start real-time feedback
      if (recognitionRef.current) {
        setInterimText('')
        setRawInterimText('')
        lastCleanedTextRef.current = ''
        if (cleanTimeoutRef.current) clearTimeout(cleanTimeoutRef.current)
        try {
          recognitionRef.current.start()
        } catch (e) {
          // Already started or busy
        }
      }

      setIsRecording(true)
    } catch (err) {
      console.error('Error starting recording:', err)
    }
  }, [cleanInterimText])

  const stopRecording = useCallback(async () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || isStoppingRef.current) {
        resolve(null)
        return
      }

      isStoppingRef.current = true
      setIsRecording(false)
      if (cleanTimeoutRef.current) clearTimeout(cleanTimeoutRef.current)

      // Stop real-time feedback
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' })
        setIsTranscribing(true)
        setInterimText('')

        try {
          const token = localStorage.getItem('zoy_auth_token')
          const response = await fetch(buildBackendUrl('/api/ai/transcribe'), {
            method: 'POST',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: audioBlob,
          })

          if (!response.ok) throw new Error('Transcription failed')

          const result = await response.json()
          actions.incrementAIAction()
          resolve(result.text)
        } catch (err) {
          console.error('Transcription error:', err)
          resolve(null)
        } finally {
          setIsTranscribing(false)
          isStoppingRef.current = false
          // Stop all tracks to release microphone
          if (mediaRecorderRef.current?.stream) {
            mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop())
          }
        }
      }

      mediaRecorderRef.current.stop()
    })
  }, [])

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      return await stopRecording()
    } else {
      await startRecording()
      return null
    }
  }, [isRecording, startRecording, stopRecording])

  return {
    isRecording,
    isTranscribing,
    interimText,
    rawInterimText,
    startRecording,
    stopRecording,
    toggleRecording,
  }
}
